import dayjs from 'dayjs';
import { match, Pattern } from 'ts-pattern';

import type { ActivityId } from '~/api/user/types';
import type { IllustrationName } from '~/components';
import type { User } from '~/types/user';

import { Activity } from '~/constants/constants';

// Activities

export const isItDeveloper = (user: User): boolean => user.activities.some(({ id }) => id === Activity.itDevelopment);
export const isPhotographer = (user: User): boolean => user.activities.some(({ id }) => id === Activity.photography);
export const isWriter = (user: User): boolean => user.activities.some(({ id }) => id === Activity.languageAndCommunication);

const activityIllustrations = {
  1: 'VisualCommunication',
  2: 'DigitalDesign',
  3: 'Photography',
  4: 'Audiovisual',
  5: 'LanguageAndCommunication',
  6: 'ItDevelopment',
  7: 'Course',
  8: 'PerformingArtist',
  9: 'InteriorStyling',
  10: 'ContentCreation',
  11: 'Freelancers',
} satisfies Record<ActivityId, IllustrationName>;

export const getIllustrationByActivityId = (id: ActivityId): IllustrationName => activityIllustrations[id];

// Account completion

export const accountCompletionSteps = ['financialInfo', 'vatRegulation', 'frameworkAgreement', 'peppol'] as const;

export const getAccountCompletionProgress = (user: User) =>
  ({
    financialInfo: !!user.financial.iban,
    vatRegulation: user.vatExempt !== null,
    frameworkAgreement: user.contractSigned && user.addendumSigned,
    peppol: user.peppol.id !== null,
  }) satisfies Record<(typeof accountCompletionSteps)[number], boolean>;

export const isAccountCompleted = (user: User) => {
  const progress = getAccountCompletionProgress(user);

  return accountCompletionSteps.every((step) => progress[step]);
};

// VAT exempt limit

export const getVatExemptLimitStatus = (user: User): 'noLimit' | 'belowLimit' | 'approachingLimit' | 'nearLimit' | 'overLimit' => {
  if (!user.vatExempt) return 'noLimit';

  const totalRevenueThisYear = user.totalFcExclVatByYear[dayjs().year()] ?? 0;

  if (totalRevenueThisYear < 20_000) return 'belowLimit';
  if (totalRevenueThisYear < 22_500) return 'approachingLimit';
  if (totalRevenueThisYear < 25_000) return 'nearLimit';

  return 'overLimit';
};

// Peppol

export const getUserPeppolStatus = (user: User) =>
  match(user.peppol)
    .with({ id: Pattern.nonNullable, supportsSelfBilling: true }, () => 'Active' as const)
    .with({ id: Pattern.nonNullable, supportsSelfBilling: false }, () => 'ActiveSelfBillingFallback' as const)
    .with({ id: null, supportsSelfBilling: false }, () => 'NotRegistered' as const)
    .exhaustive();
